import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import NotFoundPage from './NotFoundPage';
import { blogAPI } from '../services/api';
import '../styles/pages/blog.css';

// Renders /blog/categoria/:slug or /blog/tag/:slug — `kind` set via route prop.
const BlogTaxonomyPage = ({ kind }) => {
  const { slug } = useParams();
  const [taxonomy, setTaxonomy] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const taxRes = kind === 'category'
          ? await blogAPI.getCategoryBySlug(slug)
          : await blogAPI.getTagBySlug(slug);
        setTaxonomy(taxRes.data.data);

        const postsRes = await blogAPI.listPosts(
          kind === 'category' ? { category: slug, limit: 24 } : { tag: slug, limit: 24 }
        );
        setPosts(postsRes.data.data || []);
      } catch (err) {
        if (err.response?.status === 404) setNotFound(true);
        else console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, kind]);

  if (notFound) return <NotFoundPage />;
  if (loading)  return <div className="lp-theme blog-page"><Header /><div className="blog-empty">Carregando…</div><Footer /></div>;

  const label = taxonomy?.name || (kind === 'category' ? 'Categoria' : 'Tag');
  const title = taxonomy?.seo_title || `${label} — Blog LifeUp`;
  const desc  = taxonomy?.seo_description || taxonomy?.description || `Posts de ${label.toLowerCase()} no blog LifeUp.`;
  const url   = `https://lifyup.com.br/blog/${kind === 'category' ? 'categoria' : 'tag'}/${slug}`;

  return (
    <div className="lp-theme blog-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
      </Helmet>

      <Header />
      <main className="blog-container">
        <div className="blog-hero">
          <div style={{ opacity: 0.7, fontSize: 14, marginBottom: 8 }}>
            {kind === 'category' ? 'Categoria' : 'Tag'}
          </div>
          <h1>{label}</h1>
          {taxonomy?.description && <p>{taxonomy.description}</p>}
        </div>

        {posts.length === 0 ? (
          <div className="blog-empty">Nenhum post encontrado.</div>
        ) : (
          <div className="blog-grid">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="blog-card">
                {p.cover_image && <img src={p.cover_image} alt={p.title} className="blog-card__img" />}
                <div className="blog-card__body">
                  <div className="blog-card__meta">
                    {p.categories?.name && <span>{p.categories.name}</span>}
                    {p.published_at && <span>· {new Date(p.published_at).toLocaleDateString('pt-BR')}</span>}
                  </div>
                  <h2 className="blog-card__title">{p.title}</h2>
                  {p.excerpt && <p className="blog-card__excerpt">{p.excerpt}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogTaxonomyPage;
